<template>
    <div>
        <template v-if="rows && rows.length > 0">
            <div class="table">
                <div class="header-row">
                    <div @click="column.sortable? sort(key): null"
                         v-for="( column, key ) in columns"
                         :key="key"
                         :class="{sortable: column.sortable, darkergrey: currentSort===key && column.sortable}"
                         :style="{flex: column.width ? '0 0 ' + column.width : 1, textAlign: column.align}">
                        {{ column.label }}
                        <img v-if="currentSort===key && column.sortable"
                             class="image-container"
                             alt="yeah"
                             :src="(currentSortDir==='ASC')? require('../../assets/icons/globe-nav-chevron-up-darkgrey.svg') : require('../../assets/icons/globe-nav-chevron-down-darkgrey.svg')"/>
                    </div>
                </div>
                <div class="body">
                    <div v-for="(row, index) in sortedRows"
                         :key="index"
                         class="row"
                         :class="{'clickable-row': isClickable}"
                         @click="isClickable? $emit('row-clicked',row.id): null">
                        <div v-for="(column,key) in columns"
                             :key="key + index"
                             :style="{flex: column.width ? '0 0 ' + column.width : 1, justifyContent: flexAlign(column.align), wordBreak: column.noBreak ? '' : 'break-all', minHeight: rowHeight}"
                             class="entry">
                            <slot :name="'column_'+key"
                                  :data="row">
                                {{ row[key] }}
                            </slot>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="empty">
                <p>
                    {{ emptyText || $t("general.emptyTable") }}
                </p>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import {Component, Prop, Vue} from "vue-property-decorator";

export interface Column {
    label: string;
    sortable?: boolean;
    sortBy?: any;
    align?: "right" | "left" | "center";
    width?: string;
}

export interface TableListColumnConfig {
    [key: string]: Column;
}

type SortDirection = "DESC" | "ASC";

@Component
export default class TableList<RowType> extends Vue {
    @Prop() rows!: Array<RowType>;
    @Prop() groupedKey!: string;
    @Prop() itemKey!: string;
    @Prop() columns!: Column[];
    @Prop({type: Boolean, default: false}) isClickable!: boolean;
    @Prop() rowHeight!: string;
    @Prop({type: String}) emptyText!: string;

    private currentSort: keyof RowType = "" as keyof RowType;
    private currentSortDir: SortDirection = "ASC";

    created() {
        const key = this.$route.query.sortKey;
        if (key) {
            this.currentSort = key as keyof RowType;
        }
        const dir = this.$route.query.sortDir;
        if (dir) {
            this.currentSortDir = dir as SortDirection;
        }
    }

    sort(key: keyof RowType) {
        if (key === this.currentSort) {
            this.currentSortDir = this.currentSortDir === "ASC" ? "DESC" : "ASC";
        }
        this.currentSort = key;
        this.$router.push({query: {sortDir: this.currentSortDir, sortKey: this.currentSort as string}});
    }

    get sortedRows(): Array<RowType> {
        if (this.currentSort) {
            let sorting: any = this.currentSort;
            if (this.columns[sorting]?.sortBy) sorting = this.columns[sorting].sortBy;
            return this.rows.sort((a: RowType, b: RowType) => {
                if(typeof sorting === "function") return this.getSortIndex(sorting(a), sorting(b));
                //@ts-ignore
                else return this.getSortIndex(a[this.currentSort], b[this.currentSort]);
            });
        } else return this.rows;
    }

    private getSortIndex(a: string | number, b: string | number) {
        const sortIsAscending = this.currentSortDir === "ASC";
        if(typeof a === "string" && typeof b === "string") {
            a = a.toLowerCase();
            b = b.toLowerCase();
        }
        if (a < b) {
            return sortIsAscending ? -1 : 1;
        }
        if (a > b) {
            return sortIsAscending ? 1 : -1;
        }
        return 0;
    }

    get isMobile() {
        return this.$store.state.isMobile;
    }

    private flexAlign(align: string): string {
        switch (align) {
            case "left":
                return "flex-start";
            case "center":
                return "center";
            case "right":
                return "flex-end";
            default:
                return align;
        }
    }
}

</script>

<style scoped lang="scss">
.table {
    width: 100%;
    border-collapse: collapse;

    .header-row {
        background: $greyShade1;
        border-radius: 4px;
        display: flex;

        .darkergrey {
            color: $greyShade6;
        }

        > div {
            flex: 1;
            text-align: left;
            font-size: 10px;
            font-weight: bold;
            letter-spacing: 0.81px;
            text-transform: uppercase;
            color: $greyShade5;
            position: relative;
            padding: 4px 8px 7px;

            &:first-child {
                padding-left: 16px;
            }

            &:last-child {
                padding-right: 16px;
            }

            .image-container {
                width: 14px;
                height: 14px;
                position: absolute;
                bottom: 7px;
            }

            &.sortable:hover {
                cursor: pointer;
            }
        }
    }

    .body {
        .row {
            display: flex;

            &:hover {
                background-color: $greyShade1;
            }

            &:not(:last-child) {
                border-bottom: 1px solid $greyShade3;
            }

            .entry {
                flex: 1;
                padding: 10px 8px;
                font-size: 12px;
                letter-spacing: -0.19px;
                display: flex;
                align-items: center;

                &:first-child {
                    padding-left: 16px;
                }

                &:last-child {
                    padding-right: 16px;
                }
            }
        }

        .clickable-row {
            cursor: pointer;
        }

        &.grouped {
            .row {
                flex-direction: column;

                .group {
                    display: flex;

                    &:hover {
                        cursor: pointer;
                    }
                }
            }

            .grouped-rows {
                overflow: hidden;
                transition: 150ms ease-out;

                .grouped-row {
                    display: flex;
                }
            }

        }
    }
}

.empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;

    img {
        height: 160px;
        width: auto;
        margin-bottom: 24px;
    }

    p {
        margin: 0;
        font-size: 12px;
    }
}
</style>
